import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sparkles, Building2, ArrowRight, ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';

const STATUS = {
  approved: { label: 'Live in directory', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending: { label: 'Pending admin review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  rejected: { label: 'Rejected', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  suspended: { label: 'Suspended', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export const ClientEcosystemPanel = ({ api, navigate, cardBg, cardBorder }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/ecosystem/my-profile')
      .then((r) => setProfile(r.data || null))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [api]);

  return (
    <div className="space-y-5" data-testid="client-ecosystem-panel">
      {/* Hero CTA */}
      <Card className={`${cardBg} border ${cardBorder} overflow-hidden`}>
        <CardContent className="p-6 sm:p-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-[#2d2a26] via-[#3a3530] to-[#2d2a26] opacity-95" />
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-[#c8a97e]/15 blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-[#c8a97e] text-xs tracking-[0.2em] font-bold mb-3">
              <Sparkles className="w-3.5 h-3.5" /> CLAYWORKS ECOSYSTEM
            </div>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-2 leading-tight">
              The internal B2B network of Clayworks.
            </h2>
            <p className="text-[#cfc8b8] max-w-xl text-sm sm:text-base leading-relaxed mb-5">
              Discover what your fellow Clayworks members do, and let our centre managers
              introduce you. No DMs, no spam — every inquiry is brokered by your CM.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate('/ecosystem')}
                className="bg-[#c8a97e] hover:bg-[#b89a6e] text-[#2d2a26] font-semibold"
                data-testid="client-browse-directory-btn"
              >
                <Building2 className="w-4 h-4 mr-2" /> Browse Directory
              </Button>
              <Button
                onClick={() => navigate('/ecosystem/my-profile')}
                variant="outline"
                className="border-[#c8a97e] text-[#c8a97e] hover:bg-[#c8a97e]/10"
                data-testid="client-manage-profile-btn"
              >
                {profile ? 'Manage your profile' : 'List your company'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile state card */}
      {loading ? (
        <Card className={`${cardBg} border ${cardBorder}`}>
          <CardContent className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></CardContent>
        </Card>
      ) : profile ? (
        <Card className={`${cardBg} border ${cardBorder}`}>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="w-16 h-16 rounded-xl bg-[#faf6ec] border flex items-center justify-center overflow-hidden shrink-0">
                {profile.logo_url ? <img src={profile.logo_url} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-7 h-7 text-[#c8a97e]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="font-heading font-bold text-lg">{profile.company_name}</h3>
                  <Badge className={`${(STATUS[profile.status] || STATUS.pending).cls} border text-[10px]`}>
                    {(STATUS[profile.status] || STATUS.pending).label}
                  </Badge>
                  {profile.is_verified && <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mb-2">{profile.industry} · {profile.primary_venue_name}</p>
                <p className="text-sm text-muted-foreground line-clamp-2">{profile.services_summary || profile.about}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => navigate('/ecosystem/my-profile')} data-testid="client-edit-profile-btn">Edit</Button>
                {profile.status === 'approved' && (
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/ecosystem/c/${profile.slug}`)}>View live</Button>
                )}
              </div>
            </div>
            {profile.status === 'pending' && (
              <div className="mt-4 flex items-start gap-2 bg-amber-50 text-amber-900 border border-amber-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-xs">Your profile is in admin review. We'll notify you by email once it's approved and live in the directory.</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className={`${cardBg} border ${cardBorder} border-dashed`}>
          <CardContent className="p-8 text-center">
            <Building2 className="w-12 h-12 mx-auto text-[#c8a97e] mb-3" />
            <h3 className="font-heading font-bold text-lg mb-1">You're not in the directory yet.</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-5">
              Showcase what your company does to fellow Clayworks members. Profiles go through a quick admin review before going live.
            </p>
            <Button onClick={() => navigate('/ecosystem/my-profile')} className="bg-[#2d2a26] hover:bg-[#1d1a16] text-[#c8a97e]" data-testid="client-create-profile-btn">
              <Sparkles className="w-4 h-4 mr-2" /> Create your profile
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientEcosystemPanel;
